import type { Chat } from './api';
import type { ParsedMessage } from './parser';
import { loadMessages } from './archive';

export interface Hit {
  chat: Chat;
  m: ParsedMessage;
  /** The matched text, trimmed to a readable window, plus where the match sits. */
  snippet: string;
  at: number;
  len: number;
}

/**
 * Search runs here because it cannot run anywhere else: the server holds
 * ciphertext and keyed hashes, so it cannot match a word it is unable to read.
 *
 * There is no persisted index. Each chat's transcript is decrypted once, kept
 * in memory for the session, and scanned. Scanning 32,000 strings is a
 * millisecond or two — the cost is the first decrypt per chat, and the fact
 * that a reload pays it again. A stored index would avoid that and is the
 * obvious next step; it is also a second copy of the plaintext to protect, so
 * it is deliberately not the first step.
 */
export class Searcher {
  private cache = new Map<string, ParsedMessage[]>();

  constructor(private accountKey: Uint8Array) {}

  /** Drop the session's plaintext — called on sign-out. */
  clear() { this.cache.clear(); }

  private async messagesFor(chat: Chat): Promise<ParsedMessage[]> {
    if (!chat.directoryCid) return [];
    const hit = this.cache.get(chat.chatId);
    if (hit) return hit;
    const msgs = await loadMessages(this.accountKey, chat.directoryCid);
    this.cache.set(chat.chatId, msgs);
    return msgs;
  }

  /**
   * `onProgress` fires per chat so the UI can show results as they arrive
   * rather than after every chat has been decrypted. `signal` lets a newer
   * query abandon an older one mid-flight.
   */
  async run(
    chats: Chat[], query: string, limit: number,
    onProgress?: (hits: Hit[], done: number, total: number) => void,
    signal?: { aborted: boolean },
  ): Promise<Hit[]> {
    const needle = fold(query);
    if (needle.length < 2) return [];

    const all: Hit[] = [];
    let done = 0;
    for (const chat of chats) {
      if (signal?.aborted) return all;
      const msgs = await this.messagesFor(chat);
      if (signal?.aborted) return all;

      for (const m of msgs) {
        if (!m.body) continue;
        const at = fold(m.body).indexOf(needle);
        if (at < 0) continue;
        all.push({ chat, m, ...window(m.body, at, needle.length) });
        if (all.length >= limit) break;
      }
      done++;
      onProgress?.(all.slice(), done, chats.length);
      if (all.length >= limit) break;
    }
    return all;
  }
}

/** Accent- and case-insensitive: "acao" has to find "ação". The fixtures are
 *  pt-BR, where an accent is the difference between a word and a typo. */
/** Shared with the chat list filter and with in-chat search: "reuniao" has to
 *  find "reunião", and nobody types the accent when they are searching. */
export const fold = (s: string) =>
  s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();

/** A readable window around the match, with the offset re-expressed against
 *  the trimmed string so the highlight still lands on the right characters. */
function window(body: string, at: number, len: number): { snippet: string; at: number; len: number } {
  const before = 32;
  const start = Math.max(0, at - before);
  const end = Math.min(body.length, at + len + 80);
  const snippet = (start > 0 ? '…' : '') + body.slice(start, end) + (end < body.length ? '…' : '');
  return { snippet, at: at - start + (start > 0 ? 1 : 0), len };
}
